// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: ClickUp reference data (lists + members), prefetched in the
// background after connect and refreshed periodically so the create-task
// modal opens with everything already loaded. One load at a time, nothing
// while fresh, and none in the app's first seconds (see prefetch).

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {ClickUpListEntry} from '@app/features/messaging/components/KonvoClickUpListPicker';
import {CoalescedLoad} from '@app/features/platform/transport/CoalescedLoad';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {afterStartup} from '@app/features/platform/utils/StartupScheduler';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import {makeAutoObservable, runInAction} from 'mobx';

export interface ClickUpMemberEntry {
	id: number;
	username: string;
	email: string;
}

const REFRESH_INTERVAL_MS = 4 * 60 * 1000;
/** A failed load is not tried again sooner than this (the refresh interval retries too). */
const RETRY_GAP_MS = 30 * 1000;

class KonvoClickUpStore {
	lists: Array<ClickUpListEntry> = [];
	members: Array<ClickUpMemberEntry> = [];
	loaded = false;
	private loadedAt = 0;
	private readonly load = new CoalescedLoad(RETRY_GAP_MS);
	private refreshTimer: VisiblePoll | null = null;
	private cancelDeferred: (() => void) | null = null;

	constructor() {
		makeAutoObservable<this, 'loadedAt' | 'load' | 'refreshTimer' | 'cancelDeferred'>(
			this,
			{loadedAt: false, load: false, refreshTimer: false, cancelDeferred: false},
			{autoBind: true},
		);
	}

	/**
	 * Background loading + periodic refresh. Safe to call repeatedly (every chip and composer
	 * calls it on mount, and READY on every reconnect): nothing is fetched while the data is
	 * fresh, and during the app's first seconds the load waits until it has settled. Pass
	 * `urgent` when the person opened something that needs the lists now (task draft, Ask).
	 */
	prefetch(urgent = false): void {
		if (urgent) {
			this.cancelDeferred?.();
			this.cancelDeferred = null;
			if (!this.loaded || Date.now() - this.loadedAt >= REFRESH_INTERVAL_MS) void this.refresh();
			this.startPolling();
			return;
		}
		if (this.cancelDeferred) return;
		if (this.refreshTimer) {
			// Polling already; only a failed first load is worth trying again (at most every 30 s).
			if (!this.loaded) void this.refresh();
			return;
		}
		this.cancelDeferred = afterStartup(
			() => {
				this.cancelDeferred = null;
				this.startPolling();
			},
			{delayMs: 4000, jitterMs: 6000},
		);
	}

	/** Visible windows refresh every few minutes (the first run loads, unless fresh). */
	private startPolling(): void {
		if (this.refreshTimer) return;
		this.refreshTimer = new VisiblePoll(REFRESH_INTERVAL_MS, () => this.refreshIfStale());
		this.refreshTimer.start();
	}

	private refreshIfStale(): Promise<void> | undefined {
		if (this.loaded && Date.now() - this.loadedAt < REFRESH_INTERVAL_MS / 2) return undefined;
		return this.refresh();
	}

	refresh(): Promise<void> {
		return this.load.run(async () => {
			try {
				const [listsResponse, membersResponse] = await Promise.all([
					http.get<{lists: Array<ClickUpListEntry>}>(Endpoints.KONVO_CLICKUP_LISTS, RETRY_RATE_LIMITS),
					http.get<{members: Array<ClickUpMemberEntry>}>(Endpoints.KONVO_CLICKUP_MEMBERS, RETRY_RATE_LIMITS),
				]);
				runInAction(() => {
					this.lists = listsResponse.body?.lists ?? [];
					this.members = membersResponse.body?.members ?? [];
					this.loaded = true;
					this.loadedAt = Date.now();
				});
			} catch {
				// keep whatever we had; the next prefetch or interval retries
			}
		});
	}
}

export default new KonvoClickUpStore();
